<!--
 Whole screen changes history is kept internally not in browser history.

 Browser History API is used only to detect browser back button click. To achieve that two history
 states are used AFTV_KEY_BACK and AFTV_KEY_CURRENT, defined in order as written. App stays is AFTV_KEY_CURRENT
 state, but when back is clicked it is moved to AFTV_KEY_BACK which can be easily detected. Then appropriate
 state changes are computed basing on internal data and  app is pushed again to AFTV_KEY_CURRENT state.

 To make this working ALL ROUTES DEFINED IN VUE ROUTER NEEDS TO BE CALLED WITH REPLACE METHOD not with push.
 Otherwise browser history stack will have additional entries that won't be recognized by application.

 The back scenario is not trivial for this app and IS NOT HANDLED by going back to previous page.

 Going back rules:
   - From HOME screen back always exits app
   - From any root category (defined on tabs or in burger menu) back always redirects to HOME screen
   - From child category back always redirects to its parent category unless it is opened from carousel
   - If category is opened from carousel the back redirects to HOME screen (where the carousel is)
   - If carousel is expanded then back collapses carousel
   - From any non root path, like /imprint, /appInfo, /privacy back redirects to category that was shown
     before opening non root path

  To be able to compute cases above the state change is kept for further calculations. The state used here is
  actually a state change that keep information from where to which screen/category app was moved.
  On back the abandoned state is checked and based on that appropriate back scenario is applied. For example,
  if abandoned state was a change to HOME screen which simply speaking means that app is on HOME screen then
  app should be closed, because from HOME screen back always should exit app.

  There's also possibility to lock state. This is kind of marker which needs to be taken down before any other
  state change. This is for example used to handle expand/collapse carousel scenario. When carousel is expanded
  than current state is locked. When back is clicked then state has to be unlocked and this triggers carousel
  to collapse. The same happens when screen or category is changed. First locked state needs to be unlocked and
  carousel is collapsed before any other screen change.
-->
<template>
  <div></div>
</template>

<script>
import { mapState } from 'vuex'

const ROOT_PATH = '/'

const AFTV_KEY_BACK = 87282341
const AFTV_KEY_CURRENT = 58918133

/**
 * Keeps app state in a form of from/to changes.
 *
 * Root path is '/'. Under root path categories are displayed, so root path usually is followed with
 * some category, if not, than MAIN category is assumed.
 *
 * Non root path is then '/appInfo', '/privacy', '/imprint' and all other non '/' paths.
 *
 * MAIN category is the one displayed as default on HOME screen when app is opened. Such category has
 * isMain property set to true.
 *
 * PROMO category is a category displayed on promo carousel.
 *
 * HOME screen is the application landing page, the default screen user see at the beginning.
 *
 * Categories are defined in a form of tree and sometimes there would be a need to check its ancestry.
 */
class State {
  constructor (stateMixin = {}) {
    this.toPath = stateMixin.toPath || ROOT_PATH
    this.fromPath = stateMixin.fromPath || ROOT_PATH
    this.toCategory = stateMixin.toCategory
    this.fromCategory = stateMixin.fromCategory
    this.locked = false
  }

  lock = () => (this.locked = true)
  unlock = () => (this.locked = false)
  isLocked = () => this.locked === true

  // * -> /
  toRootPath = () => this.toPath === ROOT_PATH
  // / -> *
  fromRootPath = () => this.fromPath === ROOT_PATH

  // * -> /appInfo | /imprint | /privacy
  toNonRootPath = () => this.toPath !== ROOT_PATH

  // * -> (MC)
  toMainCategory = () => !this.toCategory || this.toCategory?.category?.isMain
  // (MC) -> *
  fromMainCategory = () => !this.fromCategory || this.fromCategory?.category?.isMain

  // * -> (PC)
  toPromoCategory = () => this.toCategory?.isPromo === true

  // * -> / & (MC)
  toHomeScreen = () => this.toRootPath() && this.toMainCategory()
  // / & (MC) -> *
  fromHomeScreen = () => this.fromRootPath() && this.fromMainCategory()

  // for example to 'Bluvas Brothers' from 'Shows' (BB is a child of Shows)
  toChildCategoryFromItsParent = () => this.toRootPath() && this.fromRootPath() &&
    // Main category is not treated as parent, it has special meaning - an entry point
    !this.fromMainCategory() &&
    !!this.toCategory?.category?.parentCategoryIds?.includes(this.fromCategory?.category?.id)

  // direct parent category check
  toGivenParentCategory = (category) => this.toRootPath() && this.toCategory?.category?.id === category.category?.id

  toString = () => {
    return `{ path:'${this.fromPath}'->'${this.toPath}', category:'${this.fromCategory?.category?.title}'->'${this.toCategory?.category?.title}' locked:${this.locked} }`
  }
}

export default {
  data () {
    return {
      historyStack: [],
      currentStateId: -1
    }
  },

  computed: {
    ...mapState({
      activeCategory: state => state.navigation.activeCategory
    }),
    currentState () {
      return this.historyStack[this.currentStateId]
    }
  },

  created () {
    this.storeUnsubscribe = this.$store.subscribe(mutation => {
      switch (mutation.type) {
        case 'navigation/lock':
          this.onLock()
          break
        case 'navigation/unlock':
          this.onUnlock()
          break
        case 'navigation/goToHome':
          window.location.href = '/'
          break
        case 'navigation/goBack':
          window.history.back()
          break
      }
    })

    switch (window.history.state?.aftvKey) {
      case AFTV_KEY_CURRENT:
        this.log(`(created) reusing...`)
        break
      case AFTV_KEY_BACK:
        this.log(`(created) updating...`)
        window.history.pushState({ aftvKey: AFTV_KEY_CURRENT }, '')
        break
      default:
        this.log(`(created) creating...`)
        window.history.replaceState({ aftvKey: AFTV_KEY_BACK }, '')
        window.history.pushState({ aftvKey: AFTV_KEY_CURRENT }, '')
    }

    window.addEventListener('popstate', this.popBrowserState)
  },

  beforeDestroy () {
    this.storeUnsubscribe()
    window.removeEventListener('popstate', this.popBrowserState)
  },

  watch: {
    $route (to, from) {
      // VueRouter has mode set to 'history' so it's using browser History API to handle
      // browser backward/forward button clicks. It is using 'key' property to handle route
      // forward change (backward route change doesn't set such property in history state)
      // First route initializes history stack
      const { key, ...browserHistoryState } = window.history.state || {}
      if (key || this.historyStack.length === 0) {
        this.log(`($route) '${from.path}'->'${to.path}' key:${key} state:${JSON.stringify(browserHistoryState)}`)
        this.goForward(new State({ toPath: to.path, fromPath: from.path }))
      }
    },
    activeCategory (newActiveCategory, oldActiveCategory) {
      this.log(`(activeCategory) '${oldActiveCategory?.category?.title}'->'${newActiveCategory?.category?.title}'`)
      this.goForward(new State({ toCategory: newActiveCategory, fromCategory: oldActiveCategory }))
    }
  },

  methods: {
    log (message) { console.debug(`HistoryStack: ${message}`) },

    onLock () {
      if (!this.currentState.isLocked()) {
        this.currentState.lock()
        this.log(`(onLock)`)
        this.toggleBackButton()
      }
    },

    onUnlock () {
      if (this.currentState.isLocked()) {
        this.currentState.unlock()
        this.log(`(onUnlock)`)
        this.toggleBackButton()
      }
    },

    goForward (state) {
      if (this.currentState && this.currentState.isLocked()) {
        // If current state is locked the we have to unlock it first.
        this.log(`(goForward) unlocking current state ${this.currentState}`)
        this.$store.commit('navigation/unlock')
      }
      if (state.toHomeScreen()) {
        // There's no need to keep old history, form home screen back always exits app
        this.log(`(goForward) resetting history ${state}`)
        this.historyStack = [state]
        this.currentStateId = 0
      } else {
        this.log(`(goForward) applying state ${state}`)
        this.currentStateId += 1
        this.historyStack[this.currentStateId] = state
        // There's no need to keep history after overridden state
        this.historyStack.splice(this.currentStateId + 1)
      }
      this.toggleBackButton()
    },

    goBackward () {
      if (this.currentState.isLocked()) {
        // If current state is locked the we have to unlock it first.
        this.log(`(goBackward) unlocking...`)
        this.$store.commit('navigation/unlock')
        return
      } else if (this.currentState.toHomeScreen()) {
        // If we are on HOME screen the app needs to be closed.
        this.log(`(goBackward) exiting app...`)
        window.history.back()
        return
      } else if (this.currentState.fromHomeScreen()) {
        // If current state was achieved be going from HOME screen then we have to go back to HOME.
        // It doesn't matter is we are on some other category or path lik /appInfo, /privacy...
        this.log(`(goBackward) looking for nearest to home screen...`)
        this.currentStateId = this.findNearestState(state => state.toHomeScreen())
      } else if (this.currentState.toNonRootPath()) {
        // If we are on /appInfo, /privacy or /imprint we have to go back to previously displayed category.
        // Simply speaking to first state where app was on '/' path. Why not just to previous? Because
        // from /appInfo you can go for example to /imprint.
        this.log(`(goBackward) looking for nearest to root path...`)
        this.currentStateId = this.findNearestState(state => state.toRootPath())
      } else if (this.currentState.toChildCategoryFromItsParent()) {
        // If for example we are on 'Bluvas Brothers' which is a child of 'Shows' than we have to back to the
        // first state that show 'Shows' category. Usually this is simply previous state, but for the sake of
        // consistency we are looking for the nearest state.
        this.log(`(goBackward) looking for nearest to parent category...`)
        this.currentStateId = this.findNearestState(state => state.toGivenParentCategory(this.currentState.fromCategory))
      }

      // Resetting history stack if new state points to Home screen, there's no need to keep it,
      // such history is useless, back from Home always exits app
      if (!this.currentState || this.currentState.toHomeScreen()) {
        this.log(`(applyCurrentState) resetting history  '${this.currentState}'`)
        this.historyStack = [this.currentState || new State()]
        this.currentStateId = 0
      }

      // Resetting UI to present new state
      if (this.currentState?.toCategory?.category?.id !== this.activeCategory?.category?.id) {
        this.log(`(applyCurrentState) setting category '${this.currentState.toCategory?.category?.title}'`)
        this.$store.commit('navigation/goToCategory', this.currentState?.toCategory?.category)
      }
      if (this.currentState?.toPath !== this.$router.currentRoute.path) {
        this.log(`(applyCurrentState) setting path '${this.currentState?.toPath}'`)
        this.$router.replace(this.currentState?.toPath)
      }

      this.toggleBackButton()
    },

    toggleBackButton () {
      // In some situations ap should display back button. It's pretty descriptive in what
      // situations back button should be displayed.
      const goBackAllowed =
        this.currentState.isLocked() ||
        this.currentState.toNonRootPath() ||
        this.currentState.toChildCategoryFromItsParent() ||
        this.currentState.toPromoCategory()
      this.log(`(toggleBackButton) ${goBackAllowed}`)
      this.$store.commit('navigation/goBackAllowed', goBackAllowed)
    },

    popBrowserState (event) {
      if (event.state?.aftvKey === AFTV_KEY_BACK) {
        this.log(`(popBrowserState) back pressed - abandoned state: [${this.currentStateId}] ${this.currentState}`)
        this.goBackward()
        setTimeout(() => window.history.pushState({ aftvKey: AFTV_KEY_CURRENT }, ''))
      } else {
        this.log(`(popBrowserState) unknown state:${JSON.stringify(event.state)}`)
      }
    },

    findNearestState (stateMatcher) {
      for (let i = this.currentStateId - 1; i >= 0; i--) {
        if (stateMatcher.call(this, this.historyStack[i])) {
          return i
        }
      }
      return -1
    }
  }
}
</script>
